import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import React__default, { useState, useRef, useId, useEffect, useCallback, useMemo } from "react";
import { DirectionEnum, usePlatform } from "@perxis-auto/shared";
import { AutoComplete as AutoComplete$2 } from "antd";
import hash from "object-hash";
import Button from "../Button/Button.js";
import Icon from "../Icon/Icon.js";
import Input2 from "../Input/Input.js";
import Typography from "../Typography/Typography.js";
import classnames from "../../utils/classnames.js";
import "antd/es/config-provider";
import "style-inject";
import { useProviderContext } from "../Provider/context.js";
import './AutoComplete.css';/* empty css                  */
const VALUE_SYMBOL = Symbol("optionValue");
function getHighlightedText(text, highlights) {
  if (!(highlights == null ? void 0 : highlights.length)) {
    return text;
  }
  let lastAddedLetterIndex = 0;
  const highlightedTemplate = highlights == null ? void 0 : highlights.reduce((acc, range) => {
    if (range[0] === 0 && range[1] === 0) {
      return acc;
    }
    if (range[0] !== 0) {
      acc.push(text.substring(lastAddedLetterIndex, range[0]));
    }
    acc.push(
      /* @__PURE__ */ jsx("mark", { className: classnames("perxis-auto-auto-complete__match"), children: text.substring(range[0], range[1]) }, `${range[0]}-${range[0]}`)
    );
    lastAddedLetterIndex = range[1];
    return acc;
  }, []);
  if (lastAddedLetterIndex !== text.length) {
    highlightedTemplate.push(text.substring(lastAddedLetterIndex, text.length));
  }
  return highlightedTemplate;
}
const AutoComplete = ({
  options,
  onSearch,
  onChange,
  onSelect,
  onBlur,
  onFocus,
  onDiscard,
  selected,
  className,
  errorMessage,
  placeholder,
  closeOnScroll = true
}) => {
  const { direction } = useProviderContext();
  const isRTL = direction === DirectionEnum.Rtl;
  const platform = usePlatform();
  const [isFocussed, setIsFocussed] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownOffset, setDropdownOffset] = useState([0, 0]);
  const [dropdownWidth, setDropdownWidth] = useState(0);
  const [isValueSelected, setIsValueSelected] = useState(false);
  const [error, setError] = useState(false);
  const autoCompleteRef = useRef(null);
  const containerRef = useRef(null);
  const searchButtonId = useId();
  const discardButtonId = useId();
  const [searchTerm, setSearchTerm] = useState();
  const isResizeObserverSupported = "ResizeObserver" in window;
  useEffect(() => {
    setSearchTerm(selected);
    if (selected === void 0 || selected === null) {
      setIsValueSelected(false);
      return;
    }
    setIsValueSelected(true);
  }, [selected]);
  useEffect(() => {
    if (closeOnScroll) {
      window.addEventListener("scroll", handleScroll);
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }
  }, [closeOnScroll]);
  useEffect(() => {
    var _a;
    if (!isResizeObserverSupported || !containerRef.current) {
      return;
    }
    const setDropdownSizes = (element) => {
      const computedStyle = window.getComputedStyle(element);
      const borderWidth = parseInt(computedStyle.getPropertyValue("border-width"), 10) || 0;
      const offset = isRTL ? [borderWidth, borderWidth * -1] : [borderWidth * -1, borderWidth * -1];
      setDropdownOffset(offset);
      const dropdownWidth2 = parseInt(computedStyle.getPropertyValue("width"), 10) || 0;
      setDropdownWidth(dropdownWidth2);
    };
    const autoComplete = (_a = containerRef.current.childNodes) == null ? void 0 : _a[0];
    const resizeObserver = new ResizeObserver(() => setDropdownSizes(autoComplete));
    resizeObserver.observe(autoComplete);
    setDropdownSizes(autoComplete);
    return () => resizeObserver.disconnect();
  }, [containerRef, isRTL, isResizeObserverSupported, platform]);
  const handleSearchClick = useCallback(() => {
    if (searchTerm && !options.length) {
      setError(true);
    }
  }, [options.length, searchTerm]);
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearchClick();
    }
  };
  const handleScroll = () => {
    setIsDropdownOpen(false);
  };
  const handleDiscard = useCallback(() => {
    onDiscard == null ? void 0 : onDiscard();
    setSearchTerm(void 0);
    setError(false);
  }, [onDiscard]);
  const handleAutoCompleteButtonsClick = (e) => {
    const targetElement = e.target;
    const discardEl = targetElement.closest(`[id="${CSS.escape(discardButtonId)}"]`);
    if (discardEl) {
      e.preventDefault();
    }
  };
  const optionItems = useMemo(() => {
    return options.map(
      ({
        title,
        prefix,
        prefixSeparator = "",
        suffix,
        suffixSeparator = ", ",
        titleHighlights,
        label: labelText,
        labelHighlights,
        value: optionValue
      }) => {
        const titleLayout = getHighlightedText(title, titleHighlights);
        const key = hash(optionValue);
        const value = `${key}:${title}${labelText ? `:${labelText}` : ""}`;
        const labelLayout = labelText && labelText.length ? getHighlightedText(labelText, labelHighlights) : null;
        const label = /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsxs("div", { className: classnames("perxis-auto-auto-complete__title"), children: [
            prefix && /* @__PURE__ */ jsx("span", { className: classnames("perxis-auto-auto-complete__title-prefix"), children: `${prefix}${prefixSeparator}` }),
            /* @__PURE__ */ jsx("span", { children: titleLayout }),
            suffix && /* @__PURE__ */ jsx("span", { className: classnames("perxis-auto-auto-complete__title-suffix"), children: `${suffixSeparator}${suffix}` })
          ] }),
          labelLayout && /* @__PURE__ */ jsx("div", { className: classnames("perxis-auto-auto-complete__details"), children: labelLayout })
        ] });
        return {
          key,
          value,
          label,
          searchValue: title,
          /* HACK:
           * По умолчанию AutoComplete Ant-а в качестве value использует текстовое значение.
           * Чтобы использовать произвольный объект в качестве значения используется символьное значение
           */
          [VALUE_SYMBOL]: optionValue
        };
      }
    );
  }, [options]);
  const inputButtonsTemplate = React__default.useMemo(() => {
    const searchButton = /* @__PURE__ */ jsx(
      Button,
      {
        type: "text",
        onClick: handleSearchClick,
        icon: /* @__PURE__ */ jsx(Icon, { name: "search", className: classnames("perxis-auto-auto-complete__search-icon") }),
        id: searchButtonId
      }
    );
    const discardButton = /* @__PURE__ */ jsx(
      Button,
      {
        type: "text",
        onClick: handleDiscard,
        icon: /* @__PURE__ */ jsx(Icon, { name: "cross", className: classnames("perxis-auto-auto-complete__discard-icon") }),
        id: discardButtonId
      }
    );
    const divider = /* @__PURE__ */ jsx("div", { className: classnames("perxis-auto-auto-complete__icons-divider") });
    if (Boolean(searchTerm == null ? void 0 : searchTerm.length) && searchTerm !== selected) {
      return /* @__PURE__ */ jsxs(Fragment, { children: [
        searchButton,
        " ",
        divider,
        " ",
        discardButton
      ] });
    }
    if (isValueSelected) {
      return discardButton;
    }
    return searchButton;
  }, [discardButtonId, handleDiscard, handleSearchClick, isValueSelected, searchButtonId, searchTerm, selected]);
  return /* @__PURE__ */ jsxs("div", { ref: containerRef, className: classnames("perxis-auto-auto-complete__wrapper"), children: [
    /* @__PURE__ */ jsx(
      AutoComplete$2,
      {
        open: isDropdownOpen,
        ref: autoCompleteRef,
        className: classnames(
          [
            "perxis-auto-auto-complete",
            {
              "perxis-auto-auto-complete--active": isFocussed && !error,
              "perxis-auto-auto-complete--filled": !isFocussed && !!searchTerm,
              "perxis-auto-auto-complete--error": error,
              "perxis-auto-auto-complete--dropdown-open": isDropdownOpen && options.length > 0
            }
          ],
          className
        ),
        onBlur: () => {
          onBlur == null ? void 0 : onBlur();
          setIsDropdownOpen(false);
        },
        onFocus,
        value: searchTerm,
        onChange: (title) => {
          setError(false);
          onChange == null ? void 0 : onChange(title);
        },
        popupMatchSelectWidth: dropdownWidth,
        getPopupContainer: (trigger) => trigger.parentNode,
        popupClassName: classnames("perxis-auto-auto-complete__dropdown"),
        onDropdownVisibleChange: setIsDropdownOpen,
        options: optionItems,
        onSearch: (term) => {
          setSearchTerm(term);
          onSearch == null ? void 0 : onSearch(term);
        },
        dropdownAlign: { offset: dropdownOffset },
        onSelect: (_, option) => {
          setSearchTerm(option.searchValue);
          onSelect == null ? void 0 : onSelect(option[VALUE_SYMBOL]);
        },
        children: /* @__PURE__ */ jsx(
          Input2,
          {
            variant: "borderless",
            className: classnames("perxis-auto-auto-complete__input"),
            placeholder: placeholder || "Начните печатать для поиска",
            suffix: /* @__PURE__ */ jsx(
              "div",
              {
                className: classnames("perxis-auto-auto-complete__icons-wrapper"),
                onPointerDownCapture: handleAutoCompleteButtonsClick,
                children: inputButtonsTemplate
              }
            ),
            onFocus: () => setIsFocussed(true),
            onBlur: (e) => {
              var _a;
              const targetId = (_a = e.relatedTarget) == null ? void 0 : _a.id;
              if (targetId === searchButtonId || targetId === discardButtonId) {
                return;
              }
              setIsFocussed(false);
            },
            onKeyDown: handleKeyDown
          }
        )
      }
    ),
    error && /* @__PURE__ */ jsx(Typography.Text, { level: "super-small", color: "light-colorful", className: classnames("perxis-auto-auto-complete__error"), children: errorMessage || "По вашему запросу ничего не найдено" })
  ] });
};
const AutoComplete$1 = React__default.memo(AutoComplete);
export {
  AutoComplete$1 as default
};
//# sourceMappingURL=AutoComplete.js.map
